// Session PJ2: a supplier data sheet drafted from REVIEWED lift surveys. The office ticks the lifts and the photos, types the
// supplier's address, checks what the draft would hold, and a branded two-step confirm that names the recipient and the attachments
// makes a Gmail DRAFT in info@. It is never sent from here: a person checks it in Gmail and sends it.
//
// A lift that has not been reviewed cannot be included, and says why. The project's site survey is part of the sheet ONLY ONCE IT IS
// REVIEWED (Session PJ2 review fixes, QUEUE1; the route decides, whatever is ticked): it is shown as included or left out, never as a
// box to tick, and its photos are offered only while it is included. The sheet carries no price and no customer contact.
import { useEffect, useMemo, useState } from "react";
import { api, type ProjectSurveySupplierDraftPreview, type ProjectSurveySupplierOptions } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { ConfirmButton } from "../../../components/ConfirmButton";
import { Button, buttonClasses } from "../../../components/Button";
import { Loader } from "../../../components/Loader";
import { MODAL_ACTIONS, MODAL_SECONDARY_BTN, ModalShell } from "../../../components/ModalShell";
import { formatBytes } from "../projectsUi";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green disabled:opacity-50 max-sm:min-h-11";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const checkBtnCls = buttonClasses("primary", "sm", "max-sm:w-full");

export function SupplierDraftModal({ projectId, onClose, onDrafted }: { projectId: string; onClose: () => void; onDrafted: () => void }) {
  const [options, setOptions] = useState<ProjectSurveySupplierOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // The LIFT surveys ticked. The site survey is never in this set: the route includes it once it is reviewed.
  const [surveyIds, setSurveyIds] = useState<Set<string>>(new Set());
  const [photoIds, setPhotoIds] = useState<Set<string>>(new Set());
  const [to, setTo] = useState("");
  const [preview, setPreview] = useState<ProjectSurveySupplierDraftPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [sheetBusy, setSheetBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    api.projectSurveysOffice
      .supplierOptions(projectId)
      .then((o) => {
        if (!alive) return;
        setOptions(o);
        setSurveyIds(new Set(o.lifts.filter((l) => l.eligible).map((l) => l.surveyId)));
        setPhotoIds(new Set(o.photos.filter((p) => p.defaultOn).map((p) => p.id)));
      })
      .catch((e) => {
        if (alive) setLoadError(friendlyApiError(e, "Could not load the surveys for the data sheet."));
      });
    return () => {
      alive = false;
    };
  }, [projectId]);

  // A photo is offered while its survey is in the sheet: the site survey once reviewed, a lift while it is ticked.
  const siteSurveyId = options?.site && options.site.reviewed ? options.site.surveyId : null;
  const inSheet = (surveyId: string) => surveyId === siteSurveyId || surveyIds.has(surveyId);
  // photoIds is always sent as the list ticked: an empty list attaches no photo (an omitted one would mean the default set).
  const body = useMemo(
    () => ({ surveyIds: [...surveyIds], photoIds: [...photoIds].filter((id) => options?.photos.some((p) => p.id === id && (p.surveyId === siteSurveyId || surveyIds.has(p.surveyId)))), to: to.trim() }),
    [surveyIds, photoIds, to, options, siteSurveyId],
  );
  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, id: string) => {
    setPreview(null);
    const n = new Set(set);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    setter(n);
  };
  const blocker = surveyIds.size === 0 ? "Tick at least one reviewed lift." : !/^\S+@\S+\.\S+$/.test(body.to) ? "Type the supplier's email address." : null;

  async function check() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.projectSurveysOffice.supplierDraft(projectId, { ...body, preview: true });
      if (r.preview) setPreview(r);
    } catch (e) {
      setError(friendlyApiError(e, "Could not check the draft."));
    } finally {
      setBusy(false);
    }
  }

  // Session PJ2F3: the sheet is fetched first, so a refusal (the customer's name or contact details in what was typed) is said here.
  async function lookAtSheet() {
    setSheetBusy(true);
    setError(null);
    try {
      await api.projectSurveysOffice.downloadSupplierSheet(projectId, body.surveyIds, body.photoIds);
    } catch (e) {
      setError(friendlyApiError(e, "Could not get the data sheet. Please try again."));
    } finally {
      setSheetBusy(false);
    }
  }

  async function make() {
    setBusy(true);
    setError(null);
    try {
      await api.projectSurveysOffice.supplierDraft(projectId, { ...body, preview: false });
      setDone(true);
      onDrafted();
    } catch (e) {
      setError(friendlyApiError(e, "Could not make the draft."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Draft to supplier" onClose={() => !busy && onClose()} width="max-w-lg" panelClassName="p-5 max-h-[90vh] overflow-y-auto">
      <h2 className="font-heading text-xl text-ele-strong">Draft to supplier</h2>
      <p className="mt-1 text-sm text-ele-body">
        This makes a Gmail draft with the survey data sheet for a supplier. Nothing is sent: you check the draft in Gmail and send it yourself.
      </p>
      {loadError ? <p role="alert" className="mt-3 text-sm text-ele-danger">{loadError}</p> : null}
      {!options && !loadError ? <Loader shape="inline" label="Loading the surveys..." className="mt-3" /> : null}

      {done ? (
        <>
          <p role="status" className="mt-4 rounded-ele border border-ele-green px-3 py-2 text-sm text-ele-strong">A Gmail draft is ready in info@ for you to check and send.</p>
          <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
            <button type="button" className={MODAL_SECONDARY_BTN} onClick={onClose}>Close</button>
          </div>
        </>
      ) : options ? (
        <>
          <fieldset className="mt-4">
            <legend className={labelCls}>Lifts to include</legend>
            <ul className="space-y-1">
              {options.lifts.map((l) => (
                <li key={l.surveyId}>
                  <label className="flex min-h-9 max-sm:min-h-11 items-start gap-2 text-sm text-ele-body">
                    <input type="checkbox" className="mt-1" checked={surveyIds.has(l.surveyId)} disabled={!l.eligible} onChange={() => toggle(surveyIds, setSurveyIds, l.surveyId)} />
                    <span>
                      {l.label}
                      {!l.eligible ? <span className="block text-xs text-ele-muted">{l.reason ?? "Not reviewed yet, so it cannot be included."}</span> : null}
                    </span>
                  </label>
                </li>
              ))}
              {options.site ? (
                <li className="flex min-h-9 max-sm:min-h-11 items-start gap-2 text-sm text-ele-body" data-site-included>
                  <span>
                    Site survey
                    <span className="block text-xs text-ele-muted">
                      {options.site.reviewed ? "Part of the data sheet." : (options.site.reason ?? "Not reviewed yet, so it is left out of the data sheet. Review it first to include it.")}
                    </span>
                  </span>
                </li>
              ) : null}
            </ul>
          </fieldset>

          {options.photos.length > 0 ? (
            <fieldset className="mt-4">
              <legend className={labelCls}>Photos to attach</legend>
              <ul className="space-y-1">
                {options.photos
                  .filter((p) => inSheet(p.surveyId))
                  .map((p) => (
                    <li key={p.id}>
                      <label className="flex min-h-9 max-sm:min-h-11 items-center gap-2 text-sm text-ele-body">
                        <input type="checkbox" checked={photoIds.has(p.id)} onChange={() => toggle(photoIds, setPhotoIds, p.id)} />
                        <span>
                          {p.label}
                          {p.bytes != null ? <span className="text-xs text-ele-muted"> ({formatBytes(p.bytes)})</span> : null}
                        </span>
                      </label>
                    </li>
                  ))}
              </ul>
            </fieldset>
          ) : null}

          <div className="mt-4">
            <label className={labelCls} htmlFor="supplier-to">Supplier's email address</label>
            <input
              id="supplier-to"
              type="email"
              className={inputCls}
              value={to}
              onChange={(e) => {
                setPreview(null);
                setTo(e.target.value);
              }}
            />
          </div>

          {surveyIds.size > 0 ? (
            <p className="mt-3 text-sm">
              <button type="button" className="text-ele-green underline-offset-2 hover:underline disabled:opacity-50" onClick={() => void lookAtSheet()} disabled={sheetBusy}>
                {sheetBusy ? "Getting the data sheet..." : "Download the data sheet to check it first (PDF)"}
              </button>
            </p>
          ) : null}

          {preview ? (
            <div className="mt-4 rounded-ele border border-ele-card-border p-3 text-sm text-ele-body" role="status" data-draft-check>
              <p className="text-ele-strong">A draft to {preview.to}</p>
              <p className="text-xs text-ele-muted">Subject: {preview.subject}</p>
              <ul className="mt-2 list-disc space-y-0.5 pl-5">
                {preview.attachments.map((a) => (
                  <li key={a.name}>
                    {a.name} <span className="text-xs text-ele-muted">({formatBytes(a.bytes)})</span>
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-ele-muted">Total {formatBytes(preview.totalBytes)}.</p>
              {preview.siteSurveyLeftOut ? <p className="mt-1 text-[var(--ele-amber-text)]">{preview.siteSurveyLeftOut}</p> : null}
              {preview.photosLeftOut ? (
                <p className="mt-1 text-[var(--ele-amber-text)]">The sheet and the photos are over {formatBytes(preview.limitBytes)} together, the most a Gmail draft made from here can carry, so the photos are left out and the draft says they are available on request.</p>
              ) : null}
            </div>
          ) : null}

          {blocker && !preview ? <p className="mt-3 text-xs text-ele-muted">{blocker}</p> : null}
          {error ? <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">{error}</p> : null}

          <div className={`mt-5 items-center gap-3 ${MODAL_ACTIONS}`}>
            <button type="button" className={MODAL_SECONDARY_BTN} onClick={onClose} disabled={busy}>Cancel</button>
            {preview ? (
              <ConfirmButton
                onConfirm={() => void make()}
                label="Make the Gmail draft"
                confirmLabel={`Confirm: draft to ${preview.to}?`}
                busy={busy}
                busyLabel="Making the draft..."
                className={checkBtnCls}
                armedClassName="!bg-ele-danger-fill !text-white"
                armedNote={
                  <p className="rounded-ele border border-ele-purple-accent bg-ele-surface p-2 text-xs text-ele-body">
                    Makes a Gmail draft to {preview.to} with {preview.attachments.length} attachment{preview.attachments.length === 1 ? "" : "s"} ({formatBytes(preview.totalBytes)}). It is not sent.
                  </p>
                }
              />
            ) : (
              <Button size="sm" className="max-sm:w-full" onClick={() => void check()} disabled={busy || blocker !== null}>
                {busy ? "Checking..." : "Check the draft"}
              </Button>
            )}
          </div>
        </>
      ) : null}
    </ModalShell>
  );
}
